import { useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { getProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { getKibbleCurrent, type KibbleCurrentResponse } from "~/api/kibble"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"
import { assetUrl } from "~/utils/assetUrl"
import { CheckCircle, ChevronDown, PawPrint, Sparkles, Star } from "lucide-react"
import { DashboardAskDynamoCard } from "~/components/DashboardAskDynamoCard"
import { HeroPortraitFrame } from "~/components/HeroPortraitFrame"
import { HomeScreenInstallBanner } from "~/components/HomeScreenInstallBanner"

export function DashboardPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [kibble, setKibble] = useState<KibbleCurrentResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [emailVerifiedBanner, setEmailVerifiedBanner] = useState(false)

  useEffect(() => {
    Promise.all([getProfile(), getKibbleCurrent()])
      .then(([profileData, kibbleData]) => {
        setProfile(profileData)
        setKibble(kibbleData)
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  useEffect(() => {
    if (searchParams.get("email_verified") !== "1") return
    getProfile()
      .then((data) => {
        setProfile(data)
        if (data.user.email_verified) setEmailVerifiedBanner(true)
      })
      .catch(() => {})
      .finally(() => {
        const next = new URLSearchParams(searchParams)
        next.delete("email_verified")
        setSearchParams(next, { replace: true })
      })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!emailVerifiedBanner) return
    const id = window.setTimeout(() => setEmailVerifiedBanner(false), 12_000)
    return () => window.clearTimeout(id)
  }, [emailVerifiedBanner])

  useEffect(() => {
    if (!profile?.dog_profile?.portrait || profile.dog_profile.portrait.status !== "generating") return
    const interval = window.setInterval(() => {
      getProfile()
        .then(setProfile)
        .catch(() => {})
    }, 3000)
    return () => window.clearInterval(interval)
  }, [profile?.dog_profile?.portrait?.status])

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  if (!profile) return null

  const dog = profile.dog_profile
  const dogName = dog?.name ?? "Your dog"
  const archetypeName = dog?.archetype_label ?? "Archetype"
  const archetypeIcon = dog?.archetype_icon
  const heroImage = heroPortraitUrl(archetypeIcon, dog?.portrait)

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <HomeScreenInstallBanner enableFromEmailVerification={emailVerifiedBanner} />
        {emailVerifiedBanner && (
          <div
            className="mb-4 flex gap-3 rounded-lg border border-emerald-800/20 bg-emerald-50/90 p-4 text-sm text-emerald-950 shadow-sm"
            role="status"
          >
            <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-emerald-950">Email verified</p>
              <p className="mt-1 text-emerald-900/90">
                Thanks — your address is confirmed. You&apos;re all set to use Doggy Dynamo with this account.
              </p>
              <button
                type="button"
                className="mt-3 text-sm font-medium text-emerald-900 underline decoration-emerald-800/40 underline-offset-2 hover:decoration-emerald-900"
                onClick={() => setEmailVerifiedBanner(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        {!profile.user.email_verified && (
          <div className="mb-4 rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4 text-sm text-[var(--color-text-secondary)]">
            <p className="font-medium text-[var(--color-text-primary)]">Verify your email</p>
            <p className="mt-1">
              <Link to="/verify-email" className="font-medium text-[var(--color-text-primary)] underline">
                Open the verification page
              </Link>{" "}
              to resend the message or confirm we have the right address.
            </p>
          </div>
        )}
        <div className="flex justify-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)]"
          >
            <PawPrint size={14} />
            {dogName}
            <ChevronDown size={14} />
          </button>
        </div>

        {dog ? (
          <>
            <div className="mt-4 flex justify-center">
              {archetypeIcon ? (
                <img
                  src={assetUrl(`/images/archetypes/logos/${archetypeIcon}`)}
                  alt={archetypeName}
                  className="h-20 w-20 object-contain"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[var(--color-divider)]">
                  <Star size={26} className="text-[var(--color-text-secondary)]" />
                </div>
              )}
            </div>
            <p className="mt-2 text-center text-4xl font-semibold text-[var(--color-text-primary)]">{dogName}</p>
            <p className="text-center text-xl text-[var(--color-text-secondary)]">{archetypeName}</p>

            {heroImage && (
              <HeroPortraitFrame
                className="mt-4"
                src={heroImage}
                alt={`${archetypeName} hero`}
              />
            )}

            {profile.ask_dynamo && (
              <>
                <DashboardAskDynamoCard
                  dogName={dogName}
                  dailyRemaining={profile.ask_dynamo.daily_remaining}
                  dailyLimit={profile.ask_dynamo.daily_limit}
                />
                <div className="mt-2 text-center">
                  <Link
                    to="/deeper-insights"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-ask-dynamo)] underline decoration-[var(--color-ask-dynamo)]/40 underline-offset-2 hover:decoration-[var(--color-ask-dynamo)]"
                  >
                    <Sparkles size={14} aria-hidden />
                    See what&apos;s coming next →
                  </Link>
                </div>
              </>
            )}

            <section className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4">
              {kibble?.perspective ? (
                <>
                  <p className="text-sm text-[var(--color-text-secondary)]">This Week&apos;s Perspective</p>
                  <h2 className="mt-1 text-xl font-semibold text-[var(--color-text-primary)]">
                    {kibble.perspective.title ?? `${archetypeName} Perspective`}
                  </h2>
                  <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                    Updated weekly for {dogName}
                  </p>
                  <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                    {`${kibble.perspective.body.slice(0, 140)}${kibble.perspective.body.length > 140 ? "…" : ""}`}
                  </p>
                  <Link
                    to="/perspectives"
                    className="mt-3 inline-flex rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)]"
                  >
                    Read {dogName}&apos;s Perspective
                  </Link>
                </>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-[var(--color-text-secondary)]">This Week&apos;s Perspective</p>
                  <h2 className="mt-2 text-xl font-medium text-[var(--color-text-primary)]">No perspective yet</h2>
                  <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                    Your first perspective will appear here after your next scheduled delivery.
                  </p>
                </div>
              )}
            </section>

            {dog.assessment_session_token ? (
              <div className="mt-4">
                <Link
                  to={`/results?token=${encodeURIComponent(dog.assessment_session_token)}`}
                  className="inline-flex w-full justify-center rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:opacity-90"
                >
                  View {dogName}&apos;s Full Profile
                </Link>
              </div>
            ) : null}

            <section className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4">
              <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                Suggested Tip for {dogName}
              </h3>
              {kibble?.tip ? (
                <div className="mt-2 rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-3">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">{kibble.tip.title}</p>
                  <p className="text-sm text-[var(--color-text-secondary)]">Updated this week</p>
                  <Link
                    to="/tips"
                    className="mt-2 inline-flex rounded-full bg-[var(--color-text-primary)] px-3 py-1 text-xs font-medium text-[var(--color-surface-card)]"
                  >
                    View tip
                  </Link>
                </div>
              ) : (
                <div className="mt-2 text-center">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">No tip yet</p>
                  <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                    Your first tip will appear here after your next scheduled delivery.
                  </p>
                </div>
              )}
            </section>

            <div className="mt-4 text-center">
              <Link to="/deeper-insights" className="text-[var(--color-text-primary)] underline">
                Explore what&apos;s coming →
              </Link>
            </div>
          </>
        ) : (
          <p className="mt-6 text-[var(--color-text-secondary)]">
            No dog profile yet. Complete an assessment to save your dog&apos;s profile.
          </p>
        )}

      </LoggedInScreenCard>

    </PageShell>
  )
}
